import { notEmpty } from '@eturi/util'
import { createSelector } from '@reduxjs/toolkit'
import {
	getFeatureRequirementResults,
	hasAndroidRestrixCapableDevice,
	hasFeatureCapableAndroidDevice,
	hasFeatureCapableDevice,
	hasFeatureCapableIOSDevice,
	mapRawToDevice,
} from '../model-util/index.js'
import { accountAccess$, activeChildId$, rawManagedDevices$ } from '../reducers/index.js'
import type { FeatureRequirement, ManagedDevice } from '../types/index.js'

const mapDevicesToUser = (userId: string, devices: ManagedDevice[]) =>
	devices.filter((d) => d.users?.includes(userId))

export const managedDevices$ = /*@__PURE__*/ createSelector(
	rawManagedDevices$,
	(d): ManagedDevice[] => d.map(mapRawToDevice),
)

// Gets devices that have device location enabled
export const managedGeoDevices$ = /*@__PURE__*/ createSelector(managedDevices$, (dvs) =>
	dvs.filter((d) => d.device_location_active),
)

// Paired managed devices are for non-retired devices that have users
export const pairedDevices$ = /*@__PURE__*/ createSelector(managedDevices$, (dvs) =>
	dvs.filter((d) => d.pairStatus != 'not_paired'),
)

// FIXME: Audit this. It appears to be used only for location history.
/** @deprecated */
export const nonRetiredManagedDevices$ = createSelector(managedDevices$, (dvs) =>
	dvs.filter((d) => !d.retired && notEmpty(d.users)),
)

export const hasPairedDevice$ = /*@__PURE__*/ createSelector(pairedDevices$, notEmpty)

// Determines whether user has reached max devices limit
export const hasMaxDevices$ = /*@__PURE__*/ createSelector(
	accountAccess$,
	pairedDevices$,
	(aa, d) => d.length >= aa.devices_per_account,
)

// Filters on devices belonging to the active user, including retired.
export const activeUserDevicesAll$ = /*@__PURE__*/ createSelector(
	activeChildId$,
	managedDevices$,
	mapDevicesToUser,
)

// Filters on paired devices belonging to active user
export const activeUserPairedDevices$ = /*@__PURE__*/ createSelector(
	activeChildId$,
	pairedDevices$,
	mapDevicesToUser,
)

export const activeUserHasPairedDevice$ = /*@__PURE__*/ createSelector(
	activeUserPairedDevices$,
	notEmpty,
)

export const activeUserFeatureRequirementResults$ = createSelector(
	(_: unknown, featureRequirement: FeatureRequirement) => featureRequirement,
	activeUserPairedDevices$,
	getFeatureRequirementResults,
)

export const activeUserHasFeatureCapableDevice$ = createSelector(
	(_: unknown, featureRequirement: FeatureRequirement) => featureRequirement,
	activeUserPairedDevices$,
	hasFeatureCapableDevice,
)

export const activeUserHasFeatureCapableAndroidDevice$ = createSelector(
	(_: unknown, featureRequirement: FeatureRequirement) => featureRequirement,
	activeUserPairedDevices$,
	hasFeatureCapableAndroidDevice,
)

export const activeUserHasFeatureCapableIOSDevice$ = createSelector(
	(_: unknown, featureRequirement: FeatureRequirement) => featureRequirement,
	activeUserPairedDevices$,
	hasFeatureCapableIOSDevice,
)

export const activeUserHasAndroidRestrixCapableDevice$ = createSelector(
	(_: unknown, minOSVer: number) => minOSVer,
	activeUserPairedDevices$,
	hasAndroidRestrixCapableDevice,
)

export const activeUserHasDevicePaired$ = /*@__PURE__*/ createSelector(
	activeChildId$,
	nonRetiredManagedDevices$,
	(activeUserId, nonRetiredDevices) => {
		if (!activeUserId) return false

		return nonRetiredDevices.some((d) => d.users.includes(activeUserId))
	},
)

export const pairedUserDevices$ = createSelector(
	pairedDevices$,
	(_: unknown, userId: string) => userId,
	(devices, userId) => devices.filter((d) => d.users.includes(userId)),
)

export const managedDeviceById$ = createSelector(
	managedDevices$,
	(_: unknown, deviceId: string) => deviceId,
	(devices, deviceId) => devices.find((d) => d.device_id === deviceId),
)
